import { Component, Output, EventEmitter, Input, Inject } from "@angular/core";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { mergeMap } from "rxjs/operators";

import { ButtonActions } from "../../../../app.constants";
import { StepAction } from "../../entities/step-action-entity";
import { DocumentPaButton } from "../../entities/document-pa-button";
import { DocumentPersonneAvertie, CategoriePa, EtatDocumentPa } from "../../entities/document-pa.entity";
import { SubjectRelay } from "../../../../shared/commands/subject-relay.class";
import { TraductionPaEntity } from "../../entities/traduction-pa.entity";
import { DocumentPaTraductionUpdateOrder } from '../../entities/document-pa-traduction-update-order.entity';
import { GenericUpdateTraductionStepOrder } from "../../entities/document-pa-generic-update-traduction-step-order.entity";

import { DocumentPaService } from "../../services/document-pa-service";

@Component({
    selector: "document-pa-consignes-generales",
    templateUrl: "document-pa-consignes-generales.html",
    styleUrls: ["document-pa-consignes-generales.css"],
})
export class DocumentPaConsignesGenerales {

    @Output() actionRequested = new EventEmitter<StepAction>();

    traductionsObligations!: TraductionPaEntity[];
    traductionsInterdictions!: TraductionPaEntity[];
    currentPaInstance!: DocumentPersonneAvertie;
	paEtats: typeof EtatDocumentPa = EtatDocumentPa;
	selectedInterdiction: number | null = null;
	
    actionButtons: DocumentPaButton[] = [
        new DocumentPaButton(ButtonActions.PREVIOUS, "Back", "btn btn-primary"),
        new DocumentPaButton(ButtonActions.CANCEL, "Liste des PA", "btn btn-danger"),
        new DocumentPaButton(ButtonActions.SAVE, "Sauvegarder maintenant", "btn btn-warning"),
        new DocumentPaButton(ButtonActions.NEXT, "Next", "btn btn-success"),
    ];
	
	private nextButton: DocumentPaButton = this.actionButtons.find(
	    b => b.action === ButtonActions.NEXT
	)!;
	
    constructor(@Inject(DocumentPaService) private documentPaService: DocumentPaService) {
        this.currentPa$.subscribe(
            (currentPa) => {
                if (currentPa && (currentPa.id !== -1 || currentPa.duplicatedId)) {
                    this.currentPaInstance = new DocumentPersonneAvertie();
					this.currentPaInstance.etat = currentPa.etat;
					this.currentPaInstance.id = currentPa.id;
					this.currentPaInstance.duplicatedId = currentPa.duplicatedId;
                    this.currentPaInstance.lastCompletedStep = currentPa.lastCompletedStep;
                    this.currentPaInstance.categorie = currentPa.categorie;
                    if (this.currentPaInstance.categorie) {
                        let obligations$ = this.documentPaService.getTraductions(
                            this.currentPaInstance.categorie,
                            "CONSIGNES_GENERALES_OBLIGATIONS_EE"
                        );
                        let interdictions$ = this.documentPaService.getTraductions(
                            this.currentPaInstance.categorie,
                            "CONSIGNES_GENERALES_INTERDICTIONS_EE"
                        );
						let stepData$ = this.documentPaService.getStepConsignesGeneralesData(
						    currentPa.duplicatedId ? currentPa.duplicatedId : currentPa.id
						);
                        let data$ = combineLatest(obligations$, interdictions$, stepData$).pipe(
                            mergeMap(
                                (data) => {
                                    return forkJoin({
                                        obligations: of(data[0]),
                                        interdictions: of(data[1]),
                                        stepData: of(data[2])
                                    });
                                }
                            )
                        );
                        data$.subscribe((data) => {
                            this.traductionsObligations = data.obligations as TraductionPaEntity[];
                            this.traductionsInterdictions = data.interdictions as TraductionPaEntity[];
                            if (data.stepData && data.stepData.traductions) {
                                let checkedTrads = data.stepData.traductions.filter($ => $.checked);
                                this.traductionsInterdictions.forEach(trad =>
                                    trad.checked = checkedTrads.some(checkedTrad => checkedTrad.traductionId === trad.id)
                                );
                            }
							this.syncSelectedInterdictionFromModel();
							this.updateNextButtonState();
                        });
                    }
                }
            }
        );
    }

    private currentPa$ = new SubjectRelay<DocumentPersonneAvertie>();
    @Input() set currentPa(obs: Observable<DocumentPersonneAvertie> | undefined) {
        this.currentPa$.registerSource(obs);
    }

    private saveConsignesGenerales(afterSaveAction: ButtonActions): void {
		
        const command = new GenericUpdateTraductionStepOrder();
	    command.id = this.currentPaInstance.id;

        this.traductionsObligations.forEach(item =>
            command.traductions.push(this.mapTraductionToUpdateOrder(item))
        );

        this.traductionsInterdictions.forEach(item =>
            command.traductions.push(this.mapTraductionToUpdateOrder(item))
        );

        this.documentPaService.updateConsigneSecuGeneraleStep(command).subscribe({
            next: (response) => {
                if (this.currentPaInstance) {
                    if (this.currentPaInstance.lastCompletedStep < 3) {
                        this.currentPaInstance.lastCompletedStep = 3;
                    }
                    this.currentPaInstance.id = response.id;
                }

                this.actionRequested.emit(
                    new StepAction(this.currentPaInstance, afterSaveAction)
                );
            },
            error: (err) => {
                console.error(
                    "Erreur lors de la sauvegarde des consignes générales",
                    err
                );
            },
        });
    }

    private mapTraductionToUpdateOrder(item: TraductionPaEntity): DocumentPaTraductionUpdateOrder {
        const updateOrder: DocumentPaTraductionUpdateOrder = new DocumentPaTraductionUpdateOrder();
        updateOrder.traductionId = item.id;
        updateOrder.checked = item.checked;
        return updateOrder;
    }
	
	private syncSelectedInterdictionFromModel(): void {
	    const selected = this.traductionsInterdictions?.find(
	        t => t.presentation === 'RADIOBUTTON' && t.checked
	    );
	    this.selectedInterdiction = selected ? selected.id : null;
	}

	onInterdictionRadioChange(selectedId: number): void {
	    this.traductionsInterdictions.forEach(item => {
	        if (item.presentation === 'RADIOBUTTON') {
	            item.checked = item.id === selectedId;
	        }
	    });
		this.updateNextButtonState();
	}
	
	private hasRadioInterdictions(): boolean {
	    return !!this.traductionsInterdictions?.some(
	        t => t.presentation === 'RADIOBUTTON'
	    );
	}

	private updateNextButtonState(): void {
	    this.nextButton.disabled = this.hasRadioInterdictions() && this.selectedInterdiction === null;
	}

    performAction(requestedAction: ButtonActions): void {
        switch (requestedAction) {

            case ButtonActions.CANCEL:
            case ButtonActions.RETURN_TO_LIST:
                this.actionRequested.emit(
                    new StepAction(null, ButtonActions.RETURN_TO_LIST)
                );
                break;

            case ButtonActions.PREVIOUS:
                this.actionRequested.emit(
                    new StepAction(this.currentPaInstance, ButtonActions.PREVIOUS)
                );
                break;

            case ButtonActions.SAVE:
            case ButtonActions.SAVE_DRAFT:
                this.saveConsignesGenerales(ButtonActions.RETURN_TO_LIST);
                break;

            case ButtonActions.NEXT:
				if (this.nextButton.disabled) {
				    return;
				}
				this.saveConsignesGenerales(ButtonActions.NEXT);
				break;

            default:
                break;
        }
    }
}
